// Cellery Soapbox — app shell + routing (CONTRACTS §7).
// Routes: "/" -> DocList; "/d/:docId" and "/d/:docId/:versionId" -> DocViewer;
// unknown paths redirect to "/". Unauthenticated users see the sign-in screen.

import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth";
import { Header } from "./components/Header";
import { DocList } from "./pages/DocList";
import { DocViewer } from "./pages/DocViewer";
import { CONFIDENTIAL_FOOTER } from "./export/labels";

// Publish the sticky header's measured height as `--header-h` so full-height
// surfaces (viewer, workspace, sign-in) subtract the real value instead of a
// magic constant — which stays correct as the header condenses on mobile.
function useHeaderHeightVar() {
  useEffect(() => {
    const header = document.querySelector<HTMLElement>(".app-header");
    if (!header) return;
    const root = document.documentElement;
    const apply = () =>
      root.style.setProperty(
        "--header-h",
        `${Math.round(header.getBoundingClientRect().height)}px`
      );
    apply();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(apply);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);
}

function SignIn() {
  const { signIn, error, loading } = useAuth();
  return (
    <div className="signin-screen">
      <div className="signin-main">
        <div className="signin-card">
          <img
            className="signin-logo"
            src="/brand/Cellery_wordmark_navy.png"
            alt="Cellery"
          />
          {error && (
            <div className="error-banner" role="alert">
              {error}
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary signin-btn"
            onClick={() => void signIn()}
            disabled={loading}
          >
            Sign in with Google
          </button>
        </div>
      </div>
      <p className="signin-footer">{CONFIDENTIAL_FOOTER}</p>
    </div>
  );
}

export function App() {
  const { user, loading } = useAuth();
  useHeaderHeightVar();

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        Skip to main content
      </a>
      <Header />
      <main className="main" id="main" tabIndex={-1}>
        {loading ? (
          <div className="container">
            <p className="empty-state">Loading…</p>
          </div>
        ) : user ? (
          <Routes>
            <Route path="/" element={<DocList />} />
            <Route path="/folder/:folderId" element={<DocList />} />
            <Route path="/d/:docId" element={<DocViewer />} />
            <Route path="/d/:docId/:versionId" element={<DocViewer />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
          <SignIn />
        )}
      </main>
    </div>
  );
}
